<template>
  <div class="support-page">
    <!-- Home View -->
    <template v-if="view === 'home'">
      <div class="support-header">
        <h1 class="support-title">{{ i18n.title }}</h1>
        <div class="support-search">
          <input
            v-model="searchQuery"
            type="text"
            class="support-search-input"
            :placeholder="i18n.searchPlaceholder"
          />
        </div>
      </div>

      <!-- Search results -->
      <template v-if="searchQuery.trim()">
        <div v-if="searchResults.length" class="support-search-results">
          <button
            v-for="article in searchResults"
            :key="article.slug"
            class="support-search-result"
            @click="openArticle(article.topicSlug, article.slug)"
          >
            <span class="support-search-result-topic">{{ topicName(article.topicSlug) }}</span>
            <span class="support-search-result-title">{{ article.title }}</span>
            <span class="support-search-result-summary">{{ article.summary }}</span>
          </button>
        </div>
        <div v-else class="support-no-results">
          <p>{{ i18n.noResults }}</p>
          <p class="support-no-results-hint">{{ i18n.noResultsHint }}</p>
        </div>
      </template>

      <!-- Topic grid -->
      <template v-else>
        <div class="support-topics-grid">
          <button
            v-for="topic in topics"
            :key="topic.slug"
            class="support-topic-card"
            @click="openTopic(topic.slug)"
          >
            <div class="support-topic-icon">{{ topic.icon }}</div>
            <h3 class="support-topic-name">{{ topic.name }}</h3>
            <p class="support-topic-count">{{ topic.articleCount }} {{ i18n.articles }}</p>
          </button>
        </div>
      </template>

      <!-- Contact CTA -->
      <div class="support-contact">
        <p class="support-contact-title">{{ i18n.stillNeedHelp }}</p>
        <p class="support-contact-desc">{{ i18n.stillNeedHelpDesc }}</p>
        <a :href="`mailto:${appConfig.supportEmail}`" class="btn btn-primary support-contact-btn">
          {{ i18n.contactSupport }}
        </a>
      </div>
    </template>

    <!-- Topic View -->
    <template v-else-if="view === 'topic'">
      <nav class="support-breadcrumbs">
        <button class="support-breadcrumb-link" @click="goHome">{{ i18n.title }}</button>
        <span class="support-breadcrumb-sep">/</span>
        <span class="support-breadcrumb-current">{{ currentTopicName }}</span>
      </nav>

      <h2 class="support-topic-heading">{{ currentTopicName }}</h2>

      <div class="support-article-list">
        <button
          v-for="article in topicArticles"
          :key="article.slug"
          class="support-article-link"
          @click="openArticle(currentTopicSlug!, article.slug)"
        >
          <span class="support-article-link-title">{{ article.title }}</span>
          <span class="support-article-link-summary">{{ article.summary }}</span>
        </button>
      </div>

      <button class="btn btn-secondary support-back-btn" @click="goHome">
        {{ i18n.backToHelp }}
      </button>
    </template>

    <!-- Article View -->
    <template v-else-if="view === 'article'">
      <nav class="support-breadcrumbs">
        <button class="support-breadcrumb-link" @click="goHome">{{ i18n.title }}</button>
        <span class="support-breadcrumb-sep">/</span>
        <button class="support-breadcrumb-link" @click="openTopic(currentTopicSlug!)">{{ currentTopicName }}</button>
        <span class="support-breadcrumb-sep">/</span>
        <span class="support-breadcrumb-current">{{ currentArticle?.title }}</span>
      </nav>

      <article v-if="currentArticle" class="support-article">
        <h1 class="support-article-title">{{ currentArticleTitle }}</h1>
        <div class="support-markdown" v-html="renderedContent"></div>
      </article>

      <button class="btn btn-secondary support-back-btn" @click="goHome">
        {{ i18n.backToHelp }}
      </button>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { appConfig } from '@/config';
import { useI18n } from 'vue-i18n';
import { useHead } from '@unhead/vue';
import { marked } from 'marked';

// Locale imports for support page UI strings
import supportDa from '@/i18n/support/da.json';
import supportDe from '@/i18n/support/de.json';
import supportEn from '@/i18n/support/en.json';
import supportEs from '@/i18n/support/es.json';
import supportFi from '@/i18n/support/fi.json';
import supportFr from '@/i18n/support/fr.json';
import supportIt from '@/i18n/support/it.json';
import supportNo from '@/i18n/support/no.json';
import supportSv from '@/i18n/support/sv.json';
import supportNl from '@/i18n/support/nl.json';
import supportHe from '@/i18n/support/he.json';
import supportZh from '@/i18n/support/zh.json';
import supportJa from '@/i18n/support/ja.json';

const supportLocales: Record<string, typeof supportEn> = {
  da: supportDa, de: supportDe, en: supportEn, es: supportEs,
  fi: supportFi, fr: supportFr, it: supportIt, no: supportNo, sv: supportSv,
  nl: supportNl, he: supportHe, zh: supportZh, ja: supportJa,
};

const { locale, t } = useI18n();

const i18n = computed(() => supportLocales[locale.value] || supportEn);

// Load all .md files from content/support/
const mdFiles = import.meta.glob('../content/support/**/*.md', { query: '?raw', import: 'default', eager: true });

// Topic display order + icons
const topicOrder = ['get-started', 'payments', 'tesla-connection', 'reports', 'privacy-security', 'troubleshooting', 'glossary'];
const topicIcons: Record<string, string> = {
  'get-started': '1',
  'payments': '2',
  'tesla-connection': '3',
  'reports': '4',
  'privacy-security': '5',
  'troubleshooting': '6',
  'glossary': '7',
};

// Parse frontmatter from raw markdown
interface ArticleMeta {
  title: string;
  topic: string;
  topicSlug: string;
  summary: string;
  tags: string[];
  position: number;
  slug: string;
  content: string;
  lang: string;
}

function parseFrontmatter(raw: string, filePath: string, lang: string): ArticleMeta {
  const match = raw.match(/^---\n([\s\S]*?)\n---\n([\s\S]*)$/);
  const slug = filePath.split('/').pop()?.replace('.md', '') || '';

  if (!match) {
    return { title: slug, topic: '', topicSlug: '', summary: '', tags: [], position: 0, slug, content: raw, lang };
  }

  const frontmatter = match[1];
  const content = match[2].trim();

  const get = (key: string): string => {
    const m = frontmatter.match(new RegExp(`^${key}:\\s*(.+)$`, 'm'));
    return m ? m[1].trim() : '';
  };

  const tagsMatch = frontmatter.match(/^tags:\s*\[([^\]]*)\]/m);
  const tags = tagsMatch ? tagsMatch[1].split(',').map(s => s.trim()) : [];

  return {
    title: get('title'),
    topic: get('topic'),
    topicSlug: get('topicSlug'),
    summary: get('summary'),
    tags,
    position: parseInt(get('position')) || 0,
    slug,
    content,
    lang,
  };
}

// Parse all articles with locale tag extracted from path
const allArticles = Object.entries(mdFiles).map(([path, raw]) => {
  const localeMatch = path.match(/\/support\/(\w{2})\//);
  const lang = localeMatch ? localeMatch[1] : 'en';
  return parseFrontmatter(raw as string, path, lang);
});

// Reactive: filter articles by current locale, fallback to en
const articles = computed(() => {
  const lang = locale.value;
  const localized = allArticles.filter(a => a.lang === lang);
  if (localized.length > 0) return localized;
  return allArticles.filter(a => a.lang === 'en');
});

// Translated topic name from i18n (not from frontmatter)
function topicName(slug: string): string {
  return (i18n.value.topics as Record<string, string>)[slug] || slug;
}

// Derive topics from articles, using translated names
interface Topic {
  slug: string;
  name: string;
  icon: string;
  articleCount: number;
}

const topics = computed<Topic[]>(() => {
  const countMap = new Map<string, number>();
  for (const article of articles.value) {
    countMap.set(article.topicSlug, (countMap.get(article.topicSlug) || 0) + 1);
  }

  return topicOrder
    .filter(slug => countMap.has(slug))
    .map(slug => ({
      slug,
      name: topicName(slug),
      icon: topicIcons[slug] || '?',
      articleCount: countMap.get(slug)!,
    }));
});

// View state
const view = ref<'home' | 'topic' | 'article'>('home');
const currentTopicSlug = ref<string | null>(null);
const currentArticleSlug = ref<string | null>(null);
const searchQuery = ref('');

// Computed
const currentTopicName = computed(() => {
  if (!currentTopicSlug.value) return '';
  return topicName(currentTopicSlug.value);
});

const topicArticles = computed(() => {
  if (!currentTopicSlug.value) return [];
  return articles.value
    .filter(a => a.topicSlug === currentTopicSlug.value)
    .sort((a, b) => a.position - b.position);
});

const currentArticle = computed(() => {
  if (!currentArticleSlug.value || !currentTopicSlug.value) return null;
  return articles.value.find(a => a.slug === currentArticleSlug.value && a.topicSlug === currentTopicSlug.value) || null;
});

/**
 * SEO article meta (spec 045-seo-implementation, T1.4).
 *
 * A watcher on `currentArticle` mirrors the selected article's frontmatter into
 * a small reactive snapshot; @unhead/vue then resolves that snapshot into the
 * document <head>. When no article is open (home / topic views) the snapshot is
 * null and unhead falls back to App.vue's root meta. Open Graph type is
 * `article` and the frontmatter tags become a `keywords` meta tag.
 */
interface ArticleMetaSnapshot {
  title: string;
  summary: string;
  tags: string[];
}
const articleMeta = ref<ArticleMetaSnapshot | null>(null);
watch(
  currentArticle,
  (art) => {
    articleMeta.value = art
      ? { title: art.title, summary: art.summary, tags: art.tags }
      : null;
  },
  { immediate: true },
);

useHead(() => {
  const meta = articleMeta.value;
  if (!meta) return {};
  return {
    title: meta.title,
    meta: [
      { name: 'description', content: meta.summary },
      { name: 'keywords', content: meta.tags.join(', ') },
      { property: 'og:title', content: `${meta.title} | Support` },
      { property: 'og:description', content: meta.summary },
      { property: 'og:type', content: 'article' },
    ],
  };
});

const glossaryKeys = [
  'batterySoh', 'batteryLevel', 'usableBatteryLevel', 'batteryCapacity',
  'ratedRange', 'estimatedRange', 'idealRange', 'chargeLimit',
  'chargingState', 'chargerPower', 'chargerVoltage', 'chargeEnergyAdded',
  'chargeRate', 'fastCharger', 'ccs', 'offPeakCharging',
  'scheduledDeparture', 'preconditioning', 'chargePort', 'odometer',
  'sentryMode', 'valetMode', 'dashcam', 'softwareVersion',
  'climateKeeper', 'temperature', 'tpms', 'basicWarranty',
  'batteryWarranty', 'warrantyOdometer', 'recentAlerts',
  'chargingAlerts', 'trim', 'vin', 'factoryOptions',
] as const;

const isGlossaryArticle = computed(() =>
  currentArticle.value?.topicSlug === 'glossary' && currentArticle.value?.slug === 'glossary',
);

const currentArticleTitle = computed(() =>
  isGlossaryArticle.value ? t('glossary.sectionTitle') : (currentArticle.value?.title || ''),
);

const glossaryMarkdown = computed(() => {
  const rows = glossaryKeys.map(key => {
    const term = t(`glossary.${key}.term`);
    const definition = t(`glossary.${key}.definition`);
    return `| ${term.replace(/\|/g, '\\|')} | ${definition.replace(/\|/g, '\\|')} |`;
  });
  return [
    `| ${t('apiValues.glossaryTable.Term')} | ${t('apiValues.glossaryTable.Meaning')} |`,
    '|---|---|',
    ...rows,
  ].join('\n');
});

const renderedContent = computed(() => {
  if (!currentArticle.value) return '';
  const source = isGlossaryArticle.value ? glossaryMarkdown.value : currentArticle.value.content;
  const html = marked(source) as string;
  return html.replace(/info@boxinom.com/g, appConfig.supportEmail);
});

const searchResults = computed(() => {
  const q = searchQuery.value.trim().toLowerCase();
  if (!q) return [];
  return articles.value.filter(a =>
    a.title.toLowerCase().includes(q) ||
    a.summary.toLowerCase().includes(q) ||
    a.tags.some(tag => tag.toLowerCase().includes(q))
  );
});

// Navigation
function goHome() {
  view.value = 'home';
  currentTopicSlug.value = null;
  currentArticleSlug.value = null;
  searchQuery.value = '';
  window.scrollTo(0, 0);
}

function openTopic(slug: string) {
  const topicArticleList = articles.value
    .filter(a => a.topicSlug === slug)
    .sort((a, b) => a.position - b.position);
  if (topicArticleList.length === 1) {
    openArticle(slug, topicArticleList[0].slug);
    return;
  }
  view.value = 'topic';
  currentTopicSlug.value = slug;
  currentArticleSlug.value = null;
  window.scrollTo(0, 0);
}

function openArticle(topicSlug: string, articleSlug: string) {
  view.value = 'article';
  currentTopicSlug.value = topicSlug;
  currentArticleSlug.value = articleSlug;
  window.scrollTo(0, 0);
}
</script>
